import React, { useState } from 'react';
import { observable, computed, makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { head } from 'lodash';
import { useAppStateContext } from 'src/AppState/AppState';
import { RulesTab } from 'src/components/FraudAndCompliance/AutomaticWithdrawals/Processed/RulesTab';
import {
    ProcessedWithdrawalsWrapper,
    SelectedFilterWrapper,
    MainContent,
} from 'src/components/FraudAndCompliance/AutomaticWithdrawals/Processed/AutomaticWithdrawalsProcessed/AutomaticWithdrawalsProcessed.style';
import { AutomaticWithdrawalState } from 'src/AppState/AutomaticWithdrawalState';
import { Filters } from 'src/components/FiltersNew/Filters';
import {
    FiltersState,
    FiltersStateStrategy,
    FilterType as FilterTypeBar,
} from 'src/components/FiltersNew/Filters.state';
import { AutomaticWithdrawalsTable } from 'src/components/FraudAndCompliance/AutomaticWithdrawals/Pending/AutomaticWithdrawalsPending/AutomaticWithdrawalsPending';

const labelWithNumber = (label: string, itemsNumber: number[] | null): string => {
    const checkLength = itemsNumber ?? [];
    return `${label} (${checkLength.length})`;
};
class State {
    @observable public activeFilter: string;

    public constructor() {
        this.activeFilter = 'approve';
        makeAutoObservable(this);
    }

    public setFilter(filter: string): void {
        this.activeFilter = filter;
    }
}

class FiltersStrategy implements FiltersStateStrategy {
    @observable private activeFilterId: string | null = null;

    public constructor(
        private readonly automaticWithdrawalState: AutomaticWithdrawalState,
        private readonly state: State
    ) {
        makeAutoObservable(this);
    }

    @computed public get filters(): FilterTypeBar[] {
        return [
            {
                id: 'approve',
                key: 'approve',
                label: labelWithNumber('Approved', this.automaticWithdrawalState.allListApprove),
            },
            {
                id: 'reject',
                key: 'reject',
                label: labelWithNumber('Rejected', this.automaticWithdrawalState.allListReject),
            },
            {
                id: 'review',
                key: 'review',
                label: labelWithNumber('Review in 30 Days', this.automaticWithdrawalState.reviewList),
            },
        ];
    }

    public getActiveFilterId = (): string | null => {
        const first: FilterTypeBar | undefined = head(this.filters);
        const firstAvailableFilterId = this.activeFilterId ?? first?.id ?? null;
        const activeFilter = this.filters.length > 0 ? String(firstAvailableFilterId) : null;
        return activeFilter;
    };

    public setActiveFilterId(id: string | number | null): void {
        if (typeof id === 'string') {
            this.activeFilterId = id;
            this.state.setFilter(this.activeFilterId);
        } else {
            console.error('Selected filter can not be `null` or `number`');
        }
    }
}

export const AutomaticWithdrawalsProcessed = observer(() => {
    const { automaticWithdrawalState } = useAppStateContext();
    const [localState] = useState(() => new State());
    const [filtersState] = useState(() => new FiltersState(new FiltersStrategy(automaticWithdrawalState, localState)));

    const { activeFilter } = localState;
    const { searchResultProcessedApprove, searchResultProcessedReject, processedReviewIdList } =
        automaticWithdrawalState;

    return (
        <ProcessedWithdrawalsWrapper>
            <SelectedFilterWrapper data-test='filters'>
                <Filters state={filtersState} />
            </SelectedFilterWrapper>

            <MainContent>
                {activeFilter === 'approve' && (
                    <RulesTab
                        listRules={searchResultProcessedApprove ?? automaticWithdrawalState.allListApprove}
                        selectedTab='approve'
                    />
                )}
                {activeFilter === 'reject' && (
                    <RulesTab
                        listRules={searchResultProcessedReject ?? automaticWithdrawalState.allListReject}
                        selectedTab='reject'
                    />
                )}
                {activeFilter === 'review' && <AutomaticWithdrawalsTable withdrawalIdList={processedReviewIdList} />}
            </MainContent>
        </ProcessedWithdrawalsWrapper>
    );
});
